import type {
  GlsLandSaleItem,
  UniversalSearchGroup,
  UniversalSearchItem,
  UpcomingProject,
} from '../services/api';
import type { PosterItem } from '../components/PosterCard';

function formatPosterDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function parseLocationSubtitle(subtitle: string | null): {
  area: string | null;
  district: string | null;
} {
  if (!subtitle) return { area: null, district: null };
  const parts = subtitle.split('•').map((part) => part.trim()).filter(Boolean);
  const district = parts.find((part) => /^D\d+/i.test(part)) ?? null;
  const area = parts.find((part) => part !== district) ?? null;
  return { area, district };
}

export function searchItemToProject(item: UniversalSearchItem): UpcomingProject {
  const { area, district } = parseLocationSubtitle(item.subtitle);
  return {
    id: item.id,
    name: item.title,
    developer: null,
    coverImage: item.thumbnail,
    region: area,
    area,
    district,
    address: null,
    projectType: null,
    minimumPrice: null,
    maximumPrice: null,
    upcomingSeq: null,
    launchDate: item.launchDate,
    totalUnits: null,
    topDate: null,
    hasMaterials: true,
  };
}

export function searchItemToGls(item: UniversalSearchItem): GlsLandSaleItem {
  const { area, district } = parseLocationSubtitle(item.subtitle);
  const id = Number(item.id);
  return {
    id: Number.isFinite(id) ? id : 0,
    projectId: null,
    name: item.title,
    coverImage: item.thumbnail,
    location: item.subtitle,
    region: area,
    district,
    planningArea: null,
    nearbyMrtStation: null,
    landUse: null,
    landSaleType: null,
    tenure: null,
    leasePeriod: null,
    siteAreaSqm: null,
    siteAreaSqft: null,
    grossPlotRatioSqft: null,
    housingUnits: null,
    successfulTenderer: null,
    successfulTenderPrice: null,
    uraExpectedTop: null,
    launchDate: item.launchDate,
    closingDate: null,
    awardDate: null,
    downloadLink: null,
    downloadCount: null,
    siteStatus: null,
    dateOfLaunchEst: null,
    hasMaterials: false,
  };
}

export function searchItemToPoster(item: UniversalSearchItem): PosterItem {
  return {
    id: item.id,
    title: item.title,
    date: formatPosterDate(item.createdAt ?? item.launchDate),
    cover: item.thumbnail,
    url: item.url || '#',
    urlPublic: item.urlPublic ?? null,
    filename: item.filename ?? `${item.title || item.id}.pdf`,
    downloads: item.downloads != null ? Number(item.downloads) : 0,
  };
}

export function findSearchGroup(
  groups: UniversalSearchGroup[],
  id: string,
): UniversalSearchGroup | undefined {
  return groups.find((group) => group.id === id);
}

export function mergeSearchGroups(
  groups: UniversalSearchGroup[],
  ids: string[],
): UniversalSearchItem[] {
  const seen = new Set<string>();
  const merged: UniversalSearchItem[] = [];
  for (const id of ids) {
    const group = findSearchGroup(groups, id);
    if (!group) continue;
    for (const item of group.items) {
      const key = `${group.id}:${item.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(item);
    }
  }
  return merged;
}

export function groupCount(group: UniversalSearchGroup): number {
  return group.total > 0 ? group.total : group.items.length;
}
